import { IonGrid, IonIcon, IonInput, useIonToast } from '@ionic/react';
import { reloadOutline } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';

import { IManageCustomerResponse, StripeService } from 'services/stripe';
import { useSelector } from 'react-redux';
import { IState, IUserInfo } from 'appRedux/createStore';
import { ICasinoSchema } from 'appRedux/models/casinoModels';

import useGenerateAlias from './hooks/useGenerateAlias';
import Toggle from '../../components/Toggle/Toggle';
import { toastConfig, ToastType } from '../../config/ToastConfig';
import { getSubscriptionNameAndLogo, S3_PROFILE_BASE } from 'common/common';

import ProfileMenuOption from './ProfileMenuOption';
import ProfileClubCard from './components/ProfileClubCard';
import './manage-profile.scss';
import SmartLoading from 'components/SmartLoading';
import { useEffect, useState } from 'react';

const UserProfileMenu: React.FC = () => {
  const service = new StripeService();
  const customer_id = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.customer_id
  ) as string;
  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;
  const subscriptionId = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.subscription_id
  ) as string;
  const currentPeriodEnd = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.current_period_end
  ) as string;

  const canceledAt = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.canceled_at
  ) as string;
  const shouldShowTurnOffAutoRenew = !canceledAt && isProUser;

  const { updateUserDataPublicVisible, saveAliasName, generateAliasForInput, isAliasLoading } = useGenerateAlias();
  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [aliasInput, setAliasInput] = useState('');
  const { aliasInitial2, aliasName, isPublicVisible, avatarId, avatarBgId } = useSelector<IState>(
    (state) => state.app.persistedState.userInfo
  ) as IUserInfo;
  const casinoSchema = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema
  ) as ICasinoSchema | undefined;
  const [prevIsPublicVisible, setPrevIsPublicVisible] = useState(isPublicVisible);

  useEffect(() => {
    setPrevIsPublicVisible(isPublicVisible);
  }, [isPublicVisible]);

  const [presentToast] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  const showToast = ({ type = 'success', message }: { type?: ToastType; message: string }) => {
    toastConfig.color = type;
    toastConfig['message'] = message;
    void presentToast(toastConfig);
  };

  const handlePrivacyChange = (value: boolean) => {
    if (prevIsPublicVisible !== value) {
      updateUserDataPublicVisible(value);
      setPrevIsPublicVisible(value);
    }
  };



  const goToStripe = () => {
    history.push('/auth/stripe', { previousPage: location.pathname });
  };

  const goToChangePassword = () => {
    history.push('/changepassword');
  };
  const goToEditProfile = () => {
    history.push('/editprofile');
  };
  const isInvalidAlias = (name: string | undefined) =>
    !name || name.toLowerCase().includes('invalid');

  const startEditingAlias = () => {
    setAliasInput(isInvalidAlias(aliasName) ? '' : (aliasName ?? ''));
    setIsEditingAlias(true);
  };

  const goToMyCasinos = () => {
    history.push('/myPlayerCards');
  };
  const goToUpgradePlan = () => goToStripe();
  const { logo: subscriptionLogo } = getSubscriptionNameAndLogo(isProUser);
  const isFounder = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('Founder')
  );

  return (
    <IonGrid className="manage-profile">
      <SmartLoading loading={isAliasLoading} />
      <div className="manage-profile-header">
        <div className={isFounder ? 'gold-border-wrap' : ''}>
          <div className="manage-profile-avatar-wrap">
            <div
              className="manage-profile-header-avatar"
              onClick={() => history.push('/set-icon')}
              style={
                avatarId && avatarBgId
                  ? {
                    backgroundImage: `url(${S3_PROFILE_BASE}/avatar-bg${avatarBgId}.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                  : undefined
              }
            >
              {avatarId ? (
                <img
                  src={`${S3_PROFILE_BASE}/avatar${avatarId}.png`}
                  alt="avatar"
                  className="manage-profile-header-avatar-img"
                />
              ) : (
                aliasInitial2
              )}
            </div>
            <div className="manage-profile-avatar-edit" onClick={() => history.push('/set-icon')}>
              <img src="assets/icon/pencil.svg" alt="edit" />
            </div>
          </div>
        </div>
        {isEditingAlias ? (
          <div className="alias-edit-container">
            <div className="alias-input-row">
              <button
                className="alias-clear-btn"
                onClick={() => setIsEditingAlias(false)}
              >
                ✕
              </button>
              <IonInput
                className="alias-name-input"
                value={aliasInput}
                placeholder="User Name"
                onIonChange={(e) => setAliasInput(e.detail.value ?? '')}
              />
              <button
                className="alias-save-btn"
                onClick={() => {
                  const trimmed = aliasInput.trim();
                  if (trimmed && trimmed !== aliasName) saveAliasName(trimmed);
                  setIsEditingAlias(false);
                }}
              >
                ✓
              </button>
            </div>
            <button
              className="alias-auto-generate-btn"
              onClick={() => generateAliasForInput((name) => setAliasInput(name))}
            >
              <IonIcon icon={reloadOutline} className="alias-reload-icon" />
              Auto Generate
            </button>
          </div>
        ) : (
          <div className="manage-profile-user-name" onClick={startEditingAlias}>
            {aliasName}
            <img src="assets/icon/edit-profile.svg" alt="edit" className="alias-edit-icon" />
          </div>
        )}
        <Toggle
          labelOff="Private"
          labelOn="Public"
          onChange={handlePrivacyChange}
          isChecked={isPublicVisible}
        />
        {isProUser && !shouldShowTurnOffAutoRenew && (
          <div className="margin-top-10px">
            <div className="subscribe-valid-info">
              Subscription valid till:{' '}
              {moment.unix(parseInt(currentPeriodEnd)).format('MMMM DD, YYYY')}
            </div>
          </div>
        )}
      </div>
      {casinoSchema && (
        <ProfileClubCard casino={casinoSchema} />
      )}
      <div className="manage-profile-menu">
        <ProfileMenuOption
          optionImageSrc="assets/images/dollar-icon.svg"
          optionLabel="My Player Cards"
          onClick={goToMyCasinos}
        />
        <ProfileMenuOption
          optionImageSrc="assets/images/user-icon.svg"
          optionLabel="Edit Profile"
          onClick={goToEditProfile}
        />
        <ProfileMenuOption
          optionImageSrc="assets/images/lock-icon.svg"
          optionLabel="Change Password"
          onClick={goToChangePassword}
        />
      </div>
    </IonGrid>
  );
};

export default UserProfileMenu;
